/**
 * Durable local read store for AgentChat.
 *
 * React Query is still the live in-memory view, but this IndexedDB store is the
 * phone-local read model. Query hooks read it first so cached chats render even
 * when the backend is offline, slow, or still waking up.
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import type { QueryClient, QueryKey } from '@tanstack/react-query'
import type { Chat, Message, Topic } from './apiModels'
import { mergeTopicUpdates } from './topicListUpdates'
import { mergeVisibleMessages } from './messageMerge'
import { messageIsMarkAuthoredInbound } from './messageVisibility'

type TopicScope = {
  chat_id: string
  topics: Topic[]
  updated_at: number
}

type MessageScope = {
  key: string
  chat_id: string
  topic_id: string | null
  messages: Message[]
  message_scope_version?: number | null
  updated_at: number
}

type MessageSnapshot = {
  messages: Message[]
  version: number | null
  updatedAt: number
}

type ArchivedMessage = Message & {
  archive_scope_key: string
  archive_chat_id: string
}

type OfflineArchiveMeta = {
  key: 'cursor'
  cursor: number
  complete: boolean
  pending_topic_chat_ids?: string[]
  updated_at: number
}

export type OfflineArchiveChange = {
  seq: number
  message_id: string
  chat_id: string
  topic_id: string | null
  message: Message | null
}

type OfflineArchivePage = {
  changes: OfflineArchiveChange[]
  next_cursor: number
  has_more: boolean
}

export type OfflineOlderPage = {
  messages: Message[]
  hasOlder: boolean
  archiveComplete: boolean
}

type FreshMessageOptions = {
  expectedVersion?: number | null
  maxAgeMs?: number
}

export type LocalReadStatePatch = {
  topicId?: string | null
  allTopics?: boolean
  markedRead?: number
}

interface LocalReadSchema extends DBSchema {
  chats: {
    key: string
    value: Chat
    indexes: { by_activity: number }
  }
  chat_details: {
    key: string
    value: Chat
  }
  topic_scopes: {
    key: string
    value: TopicScope
  }
  message_scopes: {
    key: string
    value: MessageScope
    indexes: { by_chat: string; by_updated: number }
  }
  meta: {
    key: string
    value: { key: string; updated_at: number }
  }
  message_archive: {
    key: string
    value: ArchivedMessage
    indexes: {
      by_scope_time: [string, number, string]
      by_chat: string
    }
  }
  offline_meta: {
    key: 'cursor'
    value: OfflineArchiveMeta
  }
}

const DB_NAME = 'agentchat-local-read-store'
const DB_VERSION = 2
const MESSAGE_LIMIT = 1000
const LOCAL_MESSAGE_WINDOW_LIMIT = 10
const LOCAL_MESSAGE_FRESH_MS = 30_000
const ATTACHMENT_CACHE = 'agentchat-attachments-v1'
const ATTACHMENT_WARM_LIMIT = 24
const MAX_WARM_ATTACHMENT_BYTES = 25 * 1024 * 1024
const OFFLINE_ARCHIVE_PAGE_LIMIT = 250
const OFFLINE_ARCHIVE_IDLE_TIMEOUT_MS = 1200
const OFFLINE_ARCHIVE_MIN_YIELD_MS = 250
const OFFLINE_ARCHIVE_LOCK = 'agentchat-offline-archive-sync'
let _dbPromise: Promise<IDBPDatabase<LocalReadSchema>> | null = null
let _persistenceInstalled = false
const _persistTimers = new Map<string, number>()
// Archive pages can repeatedly materialize the same scope tail. Mark every
// attachment URL attempted in this page lifetime before awaiting Cache/fetch,
// so overlapping warmers cannot turn one missing legacy file into hundreds of
// competing 404 requests. Transient network failures are removed and may retry.
const _attachmentWarmAttempted = new Set<string>()

// ── Synchronous in-memory snapshot ─────────────────────────────────────────
// The IDB read path is async and adds ~50–300 ms of skeleton flash even when
// data is fully cached. These maps mirror what IDB holds so React Query
// placeholderData can render instantly on chat-open / notification-tap.
// Populated on boot via hydrateLocalReadSnapshots(), on every save, and after
// any successful load. Reads are synchronous.
let _snapshotChats: Chat[] | null = null
const _snapshotChat = new Map<string, Chat>()
const _snapshotTopics = new Map<string, Topic[]>()
const _snapshotMessages = new Map<string, MessageSnapshot>()
let _hydratePromise: Promise<void> | null = null
let _hydrated = false
let _offlineArchiveSync: Promise<void> | null = null

function waitForOfflineArchiveIdleTurn(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  return new Promise((resolve) => {
    // Browser idle callbacks can fire immediately even though the API is busy.
    // Keep a real quiet gap between archive pages, then yield once more to
    // foreground rendering/input before issuing the next background request.
    window.setTimeout(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        resolve()
        return
      }
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(() => resolve(), {
          timeout: OFFLINE_ARCHIVE_IDLE_TIMEOUT_MS,
        })
        return
      }
      resolve()
    }, OFFLINE_ARCHIVE_MIN_YIELD_MS)
  })
}

async function withOfflineArchiveLock(run: () => Promise<void>): Promise<void> {
  if (typeof navigator === 'undefined' || !navigator.locks?.request) {
    await run()
    return
  }
  await navigator.locks.request(
    OFFLINE_ARCHIVE_LOCK,
    { mode: 'exclusive', ifAvailable: true },
    async (lock) => {
      if (lock) await run()
    },
  )
}

export function getSnapshotChats(): Chat[] | null {
  return _snapshotChats
}

export function getSnapshotChat(chatId: string): Chat | null {
  return _snapshotChat.get(chatId) ?? null
}

export function getSnapshotTopics(chatId: string): Topic[] | null {
  return _snapshotTopics.get(chatId) ?? null
}

export function getFreshSnapshotMessages(
  chatId: string,
  topicId: string | null = null,
  options: number | FreshMessageOptions | undefined = {},
): Message[] | null {
  const snapshot = _snapshotMessages.get(messageScopeKey(chatId, topicId))
  if (!snapshot || snapshot.messages.length === 0) return null
  const freshness = normalizeFreshMessageOptions(options)
  if (!messageScopeFresh(snapshot.updatedAt, freshness.maxAgeMs, snapshot.version, freshness.expectedVersion)) return null
  return snapshot.messages
}

export function getRenderableSnapshotMessages(
  chatId: string,
  topicId: string | null = null,
): Message[] | null {
  const snapshot = _snapshotMessages.get(messageScopeKey(chatId, topicId))
  if (!snapshot || snapshot.messages.length === 0) return null
  const messages = persistableMessages(snapshot.messages)
  return messages.length > 0 ? messages : null
}

export function snapshotIsHydrated(): boolean {
  return _hydrated
}

function setSnapshotChats(chats: Chat[]): void {
  _snapshotChats = [...chats].sort(newestFirst)
  for (const chat of chats) _snapshotChat.set(chat.id, chat)
}

function setSnapshotChat(chat: Chat): void {
  _snapshotChat.set(chat.id, chat)
  if (_snapshotChats) {
    const idx = _snapshotChats.findIndex((c) => c.id === chat.id)
    if (idx >= 0) _snapshotChats[idx] = chat
    else _snapshotChats = [chat, ..._snapshotChats]
  }
}

function setSnapshotTopics(chatId: string, topics: Topic[]): void {
  _snapshotTopics.set(chatId, topics)
}

function setSnapshotMessages(
  chatId: string,
  topicId: string | null,
  messages: Message[],
  updatedAt = Date.now(),
  version: number | null = messageScopeVersion(messages),
): void {
  _snapshotMessages.set(messageScopeKey(chatId, topicId), {
    messages,
    version,
    updatedAt,
  })
}

function db(): Promise<IDBPDatabase<LocalReadSchema>> {
  if (_dbPromise) return _dbPromise
  _dbPromise = openDB<LocalReadSchema>(DB_NAME, DB_VERSION, {
    upgrade(d) {
      if (!d.objectStoreNames.contains('chats')) {
        const chats = d.createObjectStore('chats', { keyPath: 'id' })
        chats.createIndex('by_activity', 'last_activity_at')
      }
      if (!d.objectStoreNames.contains('chat_details')) {
        d.createObjectStore('chat_details', { keyPath: 'id' })
      }
      if (!d.objectStoreNames.contains('topic_scopes')) {
        d.createObjectStore('topic_scopes', { keyPath: 'chat_id' })
      }
      if (!d.objectStoreNames.contains('message_scopes')) {
        const messages = d.createObjectStore('message_scopes', { keyPath: 'key' })
        messages.createIndex('by_chat', 'chat_id')
        messages.createIndex('by_updated', 'updated_at')
      }
      if (!d.objectStoreNames.contains('meta')) {
        d.createObjectStore('meta', { keyPath: 'key' })
      }
      if (!d.objectStoreNames.contains('message_archive')) {
        const archive = d.createObjectStore('message_archive', { keyPath: 'id' })
        archive.createIndex('by_scope_time', ['archive_scope_key', 'ts', 'id'])
        archive.createIndex('by_chat', 'archive_chat_id')
      }
      if (!d.objectStoreNames.contains('offline_meta')) {
        d.createObjectStore('offline_meta', { keyPath: 'key' })
      }
    },
  })
  return _dbPromise
}

function messageScopeKey(chatId: string, topicId: string | null = null): string {
  return `${chatId}::${topicId ?? '__all__'}`
}

function newestFirst(a: Chat, b: Chat): number {
  return (b.last_activity_at ?? b.created_at ?? 0) - (a.last_activity_at ?? a.created_at ?? 0)
}

function byMessageTime(a: Message, b: Message): number {
  return a.ts - b.ts
}

function pruneMessages(messages: Message[]): Message[] {
  if (messages.length <= MESSAGE_LIMIT) return [...messages].sort(byMessageTime)
  return [...messages]
    .sort(byMessageTime)
    .slice(-MESSAGE_LIMIT)
}

function mergeMessages(previous: Message[], next: Message[]): Message[] {
  return pruneMessages(mergeVisibleMessages(previous, next))
}

function latestInboundIndex(messages: Message[]): number {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index]
    if (messageIsMarkAuthoredInbound(message)) return index
  }
  return -1
}

function thirdLatestInboundContextIndex(messages: Message[]): number {
  let seen = 0
  let earliestInbound = -1
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (!messageIsMarkAuthoredInbound(messages[index])) continue
    earliestInbound = index
    seen += 1
    if (seen === 3) return index
  }
  return earliestInbound
}

function localDefaultMessageWindow(messages: Message[], historyScopeKey: string): Message[] {
  const ordered = pruneMessages(messages)
  const inboundIndex = latestInboundIndex(ordered)
  let startIndex = Math.max(0, ordered.length - LOCAL_MESSAGE_WINDOW_LIMIT)
  if (inboundIndex >= 0) {
    const rowsFromLatest = ordered.length - inboundIndex
    if (rowsFromLatest >= LOCAL_MESSAGE_WINDOW_LIMIT) {
      startIndex = Math.max(0, ordered.length - LOCAL_MESSAGE_WINDOW_LIMIT)
    } else {
      const contextIndex = thirdLatestInboundContextIndex(ordered)
      const remainingBeforeLatest = LOCAL_MESSAGE_WINDOW_LIMIT - rowsFromLatest
      startIndex = Math.max(0, contextIndex, inboundIndex - remainingBeforeLatest)
    }
  }
  const window = ordered.slice(startIndex, startIndex + LOCAL_MESSAGE_WINDOW_LIMIT)
  if (window.length === 0) return window
  const sourceMarker = ordered[0]
  const hasOlderMessages = startIndex > 0 || sourceMarker?.has_older_messages === true
  if (!hasOlderMessages) return window
  window[0] = {
    ...window[0],
    has_older_messages: true,
    history_scope_key: historyScopeKey,
  }
  return window
}

function isUnconfirmedAgentOutput(message: Message): boolean {
  const isAgentOutput = message.direction === 'outbound' || message.direction === 'agent'
  if (!isAgentOutput) return false
  return message.status !== 'received' && message.status !== 'replied' && message.status !== 'error'
}

function compactToolActivityReadModel(message: Message): Message {
  const inlineCount = message.activities?.length ?? 0
  const activityCount = message.activity_count ?? inlineCount
  if (inlineCount === 0 && message.activity_count === activityCount) return message
  if (inlineCount === 0 && activityCount === 0) return message
  return {
    ...message,
    activity_count: activityCount,
    activities: [],
  }
}

function persistableMessages(messages: Message[]): Message[] {
  return messages
    .filter((message) => !isUnconfirmedAgentOutput(message))
    .map(compactToolActivityReadModel)
}

function archivedMessage(
  chatId: string,
  topicId: string | null,
  message: Message,
): ArchivedMessage {
  return {
    ...message,
    archive_scope_key: messageScopeKey(chatId, topicId),
    archive_chat_id: chatId,
  }
}

function renderableArchivedMessage(message: ArchivedMessage): Message {
  const {
    archive_scope_key: _archiveScopeKey,
    archive_chat_id: _archiveChatId,
    ...renderable
  } = message
  return compactToolActivityReadModel(renderable)
}

/** Keep the complete durable archive separate from the deliberately thin
 * normal-open read model. This preserves instant 10-row opens while making
 * older history available without a network connection. */
export async function saveArchivedMessages(
  chatId: string,
  topicId: string | null,
  messages: Message[],
): Promise<void> {
  const safeMessages = persistableMessages(messages)
  if (safeMessages.length === 0) return
  try {
    const d = await db()
    const tx = d.transaction('message_archive', 'readwrite')
    const store = tx.objectStore('message_archive')
    for (const message of safeMessages) {
      await store.put(archivedMessage(chatId, topicId, message))
    }
    await tx.done
  } catch (err) {
    console.warn('offline message archive save failed:', err)
  }
}

async function applyOfflineArchivePage(page: OfflineArchivePage): Promise<void> {
  const d = await db()
  const previousMeta = await d.get('offline_meta', 'cursor')
  const archiveTx = d.transaction('message_archive', 'readwrite')
  const archive = archiveTx.objectStore('message_archive')
  const pendingTopicChatIds = new Set(previousMeta?.pending_topic_chat_ids ?? [])
  for (const change of page.changes) {
    const safeMessage = change.message && persistableMessages([change.message])[0]
    if (safeMessage) {
      await archive.put(archivedMessage(change.chat_id, change.topic_id, safeMessage))
    } else {
      await archive.delete(change.message_id)
    }
    if (change.topic_id) pendingTopicChatIds.add(change.chat_id)
  }
  await archiveTx.done

  // The archive is the complete vault, but React's first paint comes from the
  // deliberately small message_scopes store. Keep a latest local tail for
  // every scope touched by the delta so downloaded data is render-ready on
  // the next tap instead of sitting unread in another object store.
  await materializeArchiveScopeTails(page.changes)

  const previousCursor = previousMeta?.cursor ?? 0
  const nextCursor = Math.max(previousCursor, page.next_cursor)
  await d.put('offline_meta', {
    key: 'cursor',
    cursor: nextCursor,
    complete: previousMeta?.complete === true || (!page.has_more && page.next_cursor >= previousCursor),
    pending_topic_chat_ids: [...pendingTopicChatIds],
    updated_at: Date.now(),
  })
}

type ArchiveScopeTarget = {
  key: string
  chatId: string
  topicId: string | null
}

function changedArchiveScopes(changes: OfflineArchiveChange[]): ArchiveScopeTarget[] {
  const scopes = new Map<string, ArchiveScopeTarget>()
  for (const change of changes) {
    if (!change.chat_id) continue
    const key = messageScopeKey(change.chat_id, change.topic_id)
    scopes.set(key, {
      key,
      chatId: change.chat_id,
      topicId: change.topic_id,
    })
  }
  return [...scopes.values()]
}

async function archivedScopeTail(target: ArchiveScopeTarget): Promise<Message[]> {
  const d = await db()
  const tx = d.transaction('message_archive', 'readonly')
  const index = tx.objectStore('message_archive').index('by_scope_time')
  const range = IDBKeyRange.bound(
    [target.key, 0, ''],
    [target.key, Number.MAX_SAFE_INTEGER, '\uffff'],
  )
  const descending: Message[] = []
  let cursor = await index.openCursor(range, 'prev')
  while (cursor && descending.length < LOCAL_MESSAGE_WINDOW_LIMIT + 1) {
    descending.push(renderableArchivedMessage(cursor.value))
    cursor = await cursor.continue()
  }
  await tx.done

  const hasOlder = descending.length > LOCAL_MESSAGE_WINDOW_LIMIT
  const messages = persistableMessages(
    descending.slice(0, LOCAL_MESSAGE_WINDOW_LIMIT).reverse(),
  )
  if (messages.length > 0 && hasOlder) {
    messages[0] = {
      ...messages[0],
      has_older_messages: true,
      history_scope_key: target.key,
    }
  }
  return messages
}

async function materializeArchiveScopeTails(changes: OfflineArchiveChange[]): Promise<void> {
  const scopes = changedArchiveScopes(changes)
  if (scopes.length === 0) return

  const materialized = await Promise.all(scopes.map(async (target) => ({
    target,
    messages: await archivedScopeTail(target),
  })))
  const d = await db()
  const tx = d.transaction('message_scopes', 'readwrite')
  const store = tx.objectStore('message_scopes')
  const updatedAt = Date.now()
  const attachmentsToWarm: Message[] = []
  for (const { target, messages } of materialized) {
    if (messages.length === 0) {
      await store.delete(target.key)
      _snapshotMessages.delete(target.key)
      continue
    }
    const version = messageScopeVersion(messages)
    await store.put({
      key: target.key,
      chat_id: target.chatId,
      topic_id: target.topicId,
      messages,
      message_scope_version: version,
      updated_at: updatedAt,
    })
    setSnapshotMessages(target.chatId, target.topicId, messages, updatedAt, version)
    attachmentsToWarm.push(...messages)
  }
  await tx.done
  if (attachmentsToWarm.length > 0 && typeof window !== 'undefined') {
    window.setTimeout(() => { void warmLocalMessageAttachments(attachmentsToWarm) }, 0)
  }
}

async function offlineArchiveMeta(): Promise<OfflineArchiveMeta | null> {
  try {
    const d = await db()
    return (await d.get('offline_meta', 'cursor')) ?? null
  } catch {
    return null
  }
}

async function clearSyncedOfflineTopicLists(chatIds: Set<string>): Promise<void> {
  if (chatIds.size === 0) return
  const d = await db()
  const tx = d.transaction('offline_meta', 'readwrite')
  const store = tx.objectStore('offline_meta')
  const current = await store.get('cursor')
  if (current) {
    await store.put({
      ...current,
      pending_topic_chat_ids: (current.pending_topic_chat_ids ?? [])
        .filter((chatId) => !chatIds.has(chatId)),
      updated_at: Date.now(),
    })
  }
  await tx.done
}

async function syncOfflineTopicLists(chatIds: string[]): Promise<void> {
  const requestedChatIds = new Set(chatIds)
  if (requestedChatIds.size === 0) return
  const chats = await loadLocalChats()
  const activeGroupIds = new Set((chats ?? [])
    .filter((chat) => (
      chat.type === 'auto-spawn'
      && !chat.archived
      && requestedChatIds.has(chat.id)
    ))
    .map((chat) => chat.id))
  // IDs no longer present as active groups need no offline topic refresh.
  // Clear those markers now; retain active groups only when their fetch fails.
  const syncedChatIds = new Set(
    [...requestedChatIds].filter((chatId) => !activeGroupIds.has(chatId)),
  )
  for (const chatId of activeGroupIds) {
    try {
      await waitForOfflineArchiveIdleTurn()
      const response = await fetch(
        `/api/chats/${encodeURIComponent(chatId)}/topics?recent_done_days=3&recent_done_limit=30`,
        { credentials: 'include' },
      )
      if (!response.ok) continue
      await saveLocalTopics(chatId, await response.json() as Topic[])
      syncedChatIds.add(chatId)
    } catch {
      // Another reconnect/foreground sync retries the exact group.
    }
  }
  await clearSyncedOfflineTopicLists(syncedChatIds)
}

/** Download every new or changed visible message into IndexedDB. The first
 * run backfills the complete transcript in bounded pages; later runs are tiny
 * monotonic deltas. */
export async function syncOfflineArchive(): Promise<void> {
  if (_offlineArchiveSync) return _offlineArchiveSync
  if (typeof navigator !== 'undefined' && !navigator.onLine) return
  _offlineArchiveSync = withOfflineArchiveLock(async () => {
    let cursor = (await offlineArchiveMeta())?.cursor ?? 0
    for (;;) {
      await waitForOfflineArchiveIdleTurn()
      const response = await fetch(
        `/api/chats/offline-archive?after=${cursor}&limit=${OFFLINE_ARCHIVE_PAGE_LIMIT}`,
        { credentials: 'include' },
      )
      if (!response.ok) throw new Error(`offline archive sync failed (HTTP ${response.status})`)
      const page = await response.json() as OfflineArchivePage
      if (!Number.isFinite(page.next_cursor) || page.next_cursor < cursor) {
        throw new Error('offline archive returned an invalid cursor')
      }
      await applyOfflineArchivePage(page)
      if (!page.has_more) {
        const meta = await offlineArchiveMeta()
        await syncOfflineTopicLists(meta?.pending_topic_chat_ids ?? [])
        return
      }
      if (page.next_cursor === cursor) {
        throw new Error('offline archive cursor did not advance')
      }
      cursor = page.next_cursor
    }
  }).finally(() => {
    _offlineArchiveSync = null
  })
  return _offlineArchiveSync
}

export function startOfflineArchiveAutoSync(): void {
  const syncIfOnline = () => {
    if (typeof navigator === 'undefined' || navigator.onLine) {
      void syncOfflineArchive().catch((err) => {
        console.warn('offline archive sync deferred:', err)
      })
    }
  }
  syncIfOnline()
  if (typeof window !== 'undefined') {
    window.addEventListener('online', syncIfOnline)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') syncIfOnline()
    })
  }
}

export async function loadOlderArchivedMessages(
  chatId: string,
  topicId: string | null,
  beforeMessageId: string,
  limit: number,
): Promise<OfflineOlderPage> {
  try {
    const d = await db()
    const before = await d.get('message_archive', beforeMessageId)
    const scopeKey = messageScopeKey(chatId, topicId)
    const meta = await d.get('offline_meta', 'cursor')
    if (!before || before.archive_scope_key !== scopeKey) {
      return { messages: [], hasOlder: false, archiveComplete: meta?.complete === true }
    }

    const tx = d.transaction('message_archive', 'readonly')
    const index = tx.objectStore('message_archive').index('by_scope_time')
    const range = IDBKeyRange.bound(
      [scopeKey, 0, ''],
      [scopeKey, before.ts, before.id],
      false,
      true,
    )
    const descending: Message[] = []
    let cursor = await index.openCursor(range, 'prev')
    while (cursor && descending.length < limit + 1) {
      descending.push(renderableArchivedMessage(cursor.value))
      cursor = await cursor.continue()
    }
    await tx.done

    const hasOlder = descending.length > limit
    const messages = descending.slice(0, limit).reverse()
    if (messages.length > 0) {
      messages[0] = {
        ...messages[0],
        has_older_messages: hasOlder,
        history_scope_key: scopeKey,
      }
    }
    return {
      messages,
      hasOlder,
      archiveComplete: meta?.complete === true,
    }
  } catch (err) {
    console.warn('offline Load older failed:', err)
    return { messages: [], hasOlder: false, archiveComplete: false }
  }
}

/** Rebuild an exact notification window when a newer thin scope has displaced
 * its target. The archive is keyed by message id, so this also works after a
 * cold offline launch and for targets older than the normal ten-row tail. */
export async function loadArchivedMessageWindow(
  chatId: string,
  topicId: string | null,
  messageId: string,
): Promise<Message[] | null> {
  try {
    const d = await db()
    const target = await d.get('message_archive', messageId)
    const scopeKey = messageScopeKey(chatId, topicId)
    if (!target || target.archive_scope_key !== scopeKey) return null

    const tx = d.transaction('message_archive', 'readonly')
    const index = tx.objectStore('message_archive').index('by_scope_time')
    const beforeRange = IDBKeyRange.bound(
      [scopeKey, 0, ''], [scopeKey, target.ts, target.id], false, true,
    )
    const before: Message[] = []
    let cursor = await index.openCursor(beforeRange, 'prev')
    while (cursor && before.length < 4) {
      before.push(renderableArchivedMessage(cursor.value))
      cursor = await cursor.continue()
    }
    const afterRange = IDBKeyRange.bound(
      [scopeKey, target.ts, target.id],
      [scopeKey, Number.MAX_SAFE_INTEGER, '\uffff'],
      true,
      false,
    )
    const after: Message[] = []
    cursor = await index.openCursor(afterRange, 'next')
    while (cursor && after.length < 3) {
      after.push(renderableArchivedMessage(cursor.value))
      cursor = await cursor.continue()
    }
    await tx.done

    const messages = persistableMessages([
      ...before.slice(0, 3).reverse(),
      renderableArchivedMessage(target),
      ...after,
    ])
    if (messages.length > 0 && before.length > 3) {
      messages[0] = {
        ...messages[0],
        has_older_messages: true,
        history_scope_key: scopeKey,
      }
    }
    return messages.some((message) => message.id === messageId) ? messages : null
  } catch (err) {
    console.warn('offline notification target load failed:', err)
    return null
  }
}

function normalizeFreshMessageOptions(options: number | FreshMessageOptions | undefined): Required<FreshMessageOptions> {
  if (typeof options === 'number') {
    return { expectedVersion: null, maxAgeMs: options }
  }
  if (!options) {
    return { expectedVersion: null, maxAgeMs: LOCAL_MESSAGE_FRESH_MS }
  }
  return {
    expectedVersion: finiteVersion(options.expectedVersion),
    maxAgeMs: options.maxAgeMs ?? LOCAL_MESSAGE_FRESH_MS,
  }
}

function finiteVersion(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function messageScopeVersion(messages: Message[]): number | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const version = finiteVersion(messages[i]?.message_scope_version)
    if (version !== null) return version
  }
  return null
}

function messageScopeFresh(
  updatedAt: number | null | undefined,
  maxAgeMs: number,
  actualVersion: number | null | undefined,
  expectedVersion: number | null | undefined,
): boolean {
  if (expectedVersion !== null && expectedVersion !== undefined) {
    return finiteVersion(actualVersion) === expectedVersion
  }
  if (!Number.isFinite(updatedAt) || !updatedAt) return false
  return Date.now() - Number(updatedAt) <= maxAgeMs
}

export async function loadLocalChats(): Promise<Chat[] | null> {
  try {
    const d = await db()
    const chats = await d.getAll('chats')
    if (chats.length > 0) {
      const sorted = chats.sort(newestFirst)
      setSnapshotChats(sorted)
      return sorted
    }
    return (await d.get('meta', 'chats_loaded')) ? [] : null
  } catch {
    return null
  }
}

export async function saveLocalChats(chats: Chat[]): Promise<void> {
  setSnapshotChats(chats)
  try {
    const d = await db()
    const tx = d.transaction(['chats', 'chat_details', 'meta'], 'readwrite')
    const chatStore = tx.objectStore('chats')
    const detailStore = tx.objectStore('chat_details')
    const metaStore = tx.objectStore('meta')
    await chatStore.clear()
    await Promise.all([
      ...chats.flatMap((chat) => [
        chatStore.put(chat),
        detailStore.put(chat),
      ]),
      metaStore.put({ key: 'chats_loaded', updated_at: Date.now() }),
    ])
    await tx.done
  } catch (err) {
    console.warn('local read-store chats save failed:', err)
  }
}

export async function loadLocalChat(chatId: string): Promise<Chat | null> {
  try {
    const d = await db()
    const chat = (await d.get('chat_details', chatId)) ?? (await d.get('chats', chatId)) ?? null
    if (chat) setSnapshotChat(chat)
    return chat
  } catch {
    return null
  }
}

export async function saveLocalChat(chat: Chat): Promise<void> {
  setSnapshotChat(chat)
  try {
    const d = await db()
    const tx = d.transaction(['chats', 'chat_details'], 'readwrite')
    await Promise.all([
      tx.objectStore('chats').put(chat),
      tx.objectStore('chat_details').put(chat),
    ])
    await tx.done
  } catch (err) {
    console.warn('local read-store chat save failed:', err)
  }
}

export async function loadLocalTopics(chatId: string): Promise<Topic[] | null> {
  try {
    const d = await db()
    const topics = (await d.get('topic_scopes', chatId))?.topics ?? null
    if (topics) setSnapshotTopics(chatId, topics)
    return topics
  } catch {
    return null
  }
}

export async function saveLocalTopics(
  chatId: string, topics: Topic[], options: { merge?: boolean } = {},
): Promise<void> {
  setSnapshotTopics(chatId, options.merge
    ? mergeTopicUpdates(getSnapshotTopics(chatId) ?? [], topics) : topics)
  try {
    const d = await db()
    const tx = d.transaction('topic_scopes', 'readwrite')
    const store = tx.objectStore('topic_scopes')
    const current = options.merge ? (await store.get(chatId))?.topics ?? [] : []
    const saved = options.merge ? mergeTopicUpdates(current, topics) : topics
    await store.put({ chat_id: chatId, topics: saved, updated_at: Date.now() })
    await tx.done
    setSnapshotTopics(chatId, saved)
  } catch (err) {
    console.warn('local read-store topics save failed:', err)
  }
}

export async function loadFreshLocalMessages(
  chatId: string,
  topicId: string | null = null,
  options: number | FreshMessageOptions | undefined = {},
): Promise<Message[] | null> {
  return loadLocalMessagesFromStore(chatId, topicId, options)
}

export async function loadRenderableLocalMessages(
  chatId: string,
  topicId: string | null = null,
): Promise<Message[] | null> {
  const scoped = await loadLocalMessagesFromStore(chatId, topicId, undefined, { requireFresh: false })
  return scoped ?? loadLatestArchivedMessages(chatId, topicId)
}

async function loadLatestArchivedMessages(
  chatId: string,
  topicId: string | null,
): Promise<Message[] | null> {
  try {
    const renderable = await archivedScopeTail({
      key: messageScopeKey(chatId, topicId),
      chatId,
      topicId,
    })
    if (renderable.length === 0) return null
    setSnapshotMessages(chatId, topicId, renderable, Date.now(), messageScopeVersion(renderable))
    return renderable
  } catch {
    return null
  }
}

async function loadLocalMessagesFromStore(
  chatId: string,
  topicId: string | null,
  options: number | FreshMessageOptions | undefined = {},
  behavior: { requireFresh?: boolean } = { requireFresh: true },
): Promise<Message[] | null> {
  try {
    const freshness = normalizeFreshMessageOptions(options)
    const d = await db()
    const scope = await d.get('message_scopes', messageScopeKey(chatId, topicId))
    const messages = scope?.messages ?? null
    if (messages && messages.length > 0) {
      const version = finiteVersion(scope?.message_scope_version)
      if (
        behavior.requireFresh !== false &&
        !messageScopeFresh(scope?.updated_at, freshness.maxAgeMs, version, freshness.expectedVersion)
      ) return null
      const renderable = persistableMessages(messages)
      if (renderable.length === 0) return null
      setSnapshotMessages(chatId, topicId, renderable, scope?.updated_at, version)
      return renderable
    }
    return null
  } catch {
    return null
  }
}

export async function saveLocalMessages(
  chatId: string,
  topicId: string | null,
  messages: Message[],
  options: { merge?: boolean } = {},
): Promise<void> {
  try {
    const safeMessages = persistableMessages(messages)
    if (safeMessages.length === 0) return
    await saveArchivedMessages(chatId, topicId, safeMessages)
    const d = await db()
    const key = messageScopeKey(chatId, topicId)
    const previousScope = await d.get('message_scopes', key)
    const previous = options.merge
      ? (previousScope?.messages ?? [])
      : []
    const nextMessages = options.merge ? mergeMessages(previous, safeMessages) : pruneMessages(safeMessages)
    const version = messageScopeVersion(nextMessages) ?? finiteVersion(previousScope?.message_scope_version)
    const updatedAt = Date.now()
    setSnapshotMessages(chatId, topicId, nextMessages, updatedAt, version)
    await d.put('message_scopes', {
      key,
      chat_id: chatId,
      topic_id: topicId,
      messages: nextMessages,
      message_scope_version: version,
      updated_at: updatedAt,
    })
    if (typeof window !== 'undefined') {
      window.setTimeout(() => { void warmLocalMessageAttachments(nextMessages) }, 0)
    }
  } catch (err) {
    console.warn('local read-store messages save failed:', err)
  }
}

function clearUnreadMessages(messages: Message[], predicate: (message: Message) => boolean): Message[] {
  let changed = false
  const next = messages.map((message) => {
    if (!message.unread || !predicate(message)) return message
    changed = true
    return { ...message, unread: false }
  })
  return changed ? next : messages
}

function localMessageScopeMatchesPatch(
  chatType: string | null | undefined,
  patch: Required<Pick<LocalReadStatePatch, 'allTopics'>> & Pick<LocalReadStatePatch, 'topicId'>,
  scope: MessageScope,
): boolean {
  if (patch.allTopics) return true
  if (patch.topicId) return scope.topic_id === patch.topicId
  if (chatType === 'auto-spawn') return scope.topic_id === null
  return true
}

function localMessageMatchesPatch(
  chatType: string | null | undefined,
  patch: Required<Pick<LocalReadStatePatch, 'allTopics'>> & Pick<LocalReadStatePatch, 'topicId'>,
  message: Message,
): boolean {
  if (patch.allTopics) return true
  if (patch.topicId) return message.topic_id === patch.topicId
  if (chatType === 'auto-spawn') return (message.topic_id ?? null) === null
  return true
}

function patchedChatUnread(
  chat: Chat,
  topics: Topic[] | null,
  patch: Required<Pick<LocalReadStatePatch, 'allTopics'>> & Pick<LocalReadStatePatch, 'topicId' | 'markedRead'>,
): Chat {
  const currentUnread = chat.unread ?? 0
  if (currentUnread <= 0) return chat
  if (patch.allTopics) return { ...chat, unread: 0 }

  if (patch.topicId) {
    const topicUnread = topics?.find((topic) => topic.id === patch.topicId)?.unread
    const decrement = typeof topicUnread === 'number' ? topicUnread : patch.markedRead
    return typeof decrement === 'number'
      ? { ...chat, unread: Math.max(0, currentUnread - decrement) }
      : chat
  }

  const topicBackedChat = (topics?.length ?? 0) > 0 || chat.type === 'auto-spawn'
  if (!topicBackedChat) return { ...chat, unread: 0 }

  const remainingTopicUnread = (topics ?? []).reduce((sum, topic) => sum + (topic.unread ?? 0), 0)
  if (typeof patch.markedRead === 'number') {
    return { ...chat, unread: Math.max(remainingTopicUnread, currentUnread - patch.markedRead) }
  }
  return chat
}

/**
 * Mirror a successful server read receipt into the durable phone-local read
 * model. React Query covers the currently mounted view; this keeps the next
 * cold render from repainting old unread badges out of IndexedDB.
 */
export async function markLocalReadState(chatId: string, patch: LocalReadStatePatch = {}): Promise<void> {
  const normalizedPatch = {
    topicId: patch.topicId ?? null,
    allTopics: patch.allTopics === true,
    markedRead: typeof patch.markedRead === 'number' && Number.isFinite(patch.markedRead)
      ? Math.max(0, Math.floor(patch.markedRead))
      : undefined,
  }
  try {
    const d = await db()
    const [listChat, detailChat, topicScope, messageScopes] = await Promise.all([
      d.get('chats', chatId),
      d.get('chat_details', chatId),
      d.get('topic_scopes', chatId),
      d.getAllFromIndex('message_scopes', 'by_chat', chatId),
    ])
    const chat = detailChat ?? listChat ?? null
    const chatType = chat?.type ?? null
    const topics = topicScope?.topics ?? null
    const tx = d.transaction(['chats', 'chat_details', 'topic_scopes', 'message_scopes'], 'readwrite')
    const chatsStore = tx.objectStore('chats')
    const detailsStore = tx.objectStore('chat_details')
    const topicsStore = tx.objectStore('topic_scopes')
    const messagesStore = tx.objectStore('message_scopes')
    const writes: Promise<unknown>[] = []

    if (listChat) {
      const next = patchedChatUnread(listChat, topics, normalizedPatch)
      if (next !== listChat) writes.push(chatsStore.put(next))
      setSnapshotChat(next)
    }
    if (detailChat) {
      const next = patchedChatUnread(detailChat, topics, normalizedPatch)
      if (next !== detailChat) writes.push(detailsStore.put(next))
      setSnapshotChat(next)
    }

    if (topicScope && topics) {
      const nextTopics = topics.map((topic) => (
        normalizedPatch.allTopics || topic.id === normalizedPatch.topicId
          ? { ...topic, unread: 0 }
          : topic
      ))
      writes.push(topicsStore.put({ ...topicScope, topics: nextTopics, updated_at: Date.now() }))
      setSnapshotTopics(chatId, nextTopics)
    }

    for (const scope of messageScopes) {
      if (!localMessageScopeMatchesPatch(chatType, normalizedPatch, scope)) continue
      const nextMessages = clearUnreadMessages(
        scope.messages,
        (message) => localMessageMatchesPatch(chatType, normalizedPatch, message),
      )
      if (nextMessages === scope.messages) continue
      const updatedAt = Date.now()
      writes.push(messagesStore.put({ ...scope, messages: nextMessages, updated_at: updatedAt }))
      setSnapshotMessages(chatId, scope.topic_id, nextMessages, updatedAt, finiteVersion(scope.message_scope_version))
    }

    await Promise.all(writes)
    await tx.done
  } catch (err) {
    console.warn('local read-store mark-read mirror failed:', err)
  }
}

async function saveLocalMessagesSnapshot(
  chatId: string,
  topicId: string | null,
  messages: Message[],
): Promise<void> {
  const safeMessages = persistableMessages(messages)
  if (safeMessages.length === 0) return
  await saveArchivedMessages(chatId, topicId, safeMessages)
  try {
    const d = await db()
    const key = messageScopeKey(chatId, topicId)
    const previousScope = await d.get('message_scopes', key)
    const previous = previousScope?.messages ?? []
    const incomingVersion = messageScopeVersion(safeMessages)
    if (previous.length > 0 && safeMessages.length < previous.length && incomingVersion === null) return
    if (previous.length === 0 && safeMessages.length < 2 && incomingVersion === null) return
    const nextMessages = localDefaultMessageWindow(mergeMessages(previous, safeMessages), key)
    const version = messageScopeVersion(nextMessages) ?? finiteVersion(previousScope?.message_scope_version)
    const updatedAt = Date.now()
    setSnapshotMessages(chatId, topicId, nextMessages, updatedAt, version)
    await d.put('message_scopes', {
      key,
      chat_id: chatId,
      topic_id: topicId,
      messages: nextMessages,
      message_scope_version: version,
      updated_at: updatedAt,
    })
    if (typeof window !== 'undefined') {
      window.setTimeout(() => { void warmLocalMessageAttachments(nextMessages) }, 0)
    }
  } catch (err) {
    console.warn('local read-store message snapshot save failed:', err)
  }
}

/**
 * Snapshot-only write — for SW message ingest. Use when we want React Query
 * placeholderData and live `useMessages` queries to pick up new messages
 * synchronously, but the IDB write is happening on another path (e.g. the SW
 * itself already persisted the scope).
 */
export function setLocalMessagesSnapshot(
  chatId: string,
  topicId: string | null,
  messages: Message[],
): void {
  const safeMessages = persistableMessages(messages)
  if (safeMessages.length === 0) return
  setSnapshotMessages(chatId, topicId, safeMessages)
}

/**
 * Pre-load every snapshot scope from IDB into the in-memory maps so that the
 * next `useMessages` / `useChat` / `useTopics` call renders without an async
 * IDB round-trip. Called at app boot from main.tsx; idempotent.
 */
export async function hydrateLocalReadSnapshots(): Promise<void> {
  if (_hydratePromise) return _hydratePromise
  _hydratePromise = (async () => {
    try {
      const d = await db()
      const tx = d.transaction(['chats', 'chat_details', 'topic_scopes', 'message_scopes'], 'readonly')
      const [chats, chatDetails, topicScopes, messageScopes] = await Promise.all([
        tx.objectStore('chats').getAll(),
        tx.objectStore('chat_details').getAll(),
        tx.objectStore('topic_scopes').getAll(),
        tx.objectStore('message_scopes').getAll(),
      ])
      await tx.done
      if (chatDetails.length > 0) {
        for (const chat of chatDetails) _snapshotChat.set(chat.id, chat)
      }
      if (chats.length > 0) {
        _snapshotChats = [...chats].sort(newestFirst)
        for (const chat of chats) {
          if (!_snapshotChat.has(chat.id)) _snapshotChat.set(chat.id, chat)
        }
      }
      for (const scope of topicScopes) {
        _snapshotTopics.set(scope.chat_id, scope.topics)
      }
      for (const scope of messageScopes) {
        if (scope.messages && scope.messages.length > 0) {
          _snapshotMessages.set(scope.key, {
            messages: scope.messages,
            version: finiteVersion(scope.message_scope_version),
            updatedAt: scope.updated_at,
          })
        }
      }
    } catch (err) {
      console.warn('local read-store hydrate failed:', err)
    } finally {
      _hydrated = true
    }
  })()
  return _hydratePromise
}

/**
 * Targeted boot hydrate for notification opens. A push tap already tells us
 * the chat/topic that must render, so do not block React on every cached scope
 * in the vault before mounting the thread.
 */
export async function hydrateLocalReadSnapshotScope(
  chatId: string,
  topicId: string | null,
): Promise<void> {
  try {
    const d = await db()
    const messageKey = messageScopeKey(chatId, topicId)
    const tx = d.transaction(['chats', 'chat_details', 'topic_scopes', 'message_scopes'], 'readonly')
    const [chat, chatDetail, topicScope, messageScope] = await Promise.all([
      tx.objectStore('chats').get(chatId),
      tx.objectStore('chat_details').get(chatId),
      tx.objectStore('topic_scopes').get(chatId),
      tx.objectStore('message_scopes').get(messageKey),
    ])
    await tx.done
    if (chatDetail) setSnapshotChat(chatDetail)
    else if (chat) setSnapshotChat(chat)
    if (topicScope?.topics) setSnapshotTopics(chatId, topicScope.topics)
    if (messageScope?.messages && messageScope.messages.length > 0) {
      setSnapshotMessages(
        chatId,
        topicId,
        messageScope.messages,
        messageScope.updated_at,
        finiteVersion(messageScope.message_scope_version),
      )
    } else {
      // Complete archive rows may predate the thin first-paint scope (or may
      // have been written by an older build). Notification boot must still
      // hydrate that durable data before React mounts.
      await loadLatestArchivedMessages(chatId, topicId)
    }
  } catch (err) {
    console.warn('local read-store targeted hydrate failed:', err)
  }
}

export async function warmLocalMessageAttachments(messages: Message[]): Promise<number> {
  if (typeof window === 'undefined' || !('caches' in window)) return 0
  if (typeof navigator !== 'undefined' && !navigator.onLine) return 0
  const candidates = messages
    .flatMap((message) => message.attachments ?? [])
    .filter((att) => (
      (
        att.kind === 'image' ||
        att.kind === 'video' ||
        att.kind === 'audio' ||
        att.kind === 'voice' ||
        att.kind === 'document' ||
        att.kind === 'pdf' ||
        (att.mime_type ?? '').startsWith('image/') ||
        (att.mime_type ?? '').startsWith('video/') ||
        (att.mime_type ?? '').startsWith('audio/') ||
        att.mime_type === 'application/pdf' ||
        att.mime_type === 'application/octet-stream'
      ) &&
      (att.size_bytes == null || att.size_bytes <= MAX_WARM_ATTACHMENT_BYTES)
    ))
    .slice(-ATTACHMENT_WARM_LIMIT)

  let warmed = 0
  await Promise.all(candidates.map(async (att) => {
    const attachmentPath = att.url && !att.url.startsWith('blob:')
      ? att.url
      : `/api/attachments/${encodeURIComponent(att.id)}/file`
    const url = new URL(attachmentPath, window.location.origin).toString()
    if (_attachmentWarmAttempted.has(url)) return
    _attachmentWarmAttempted.add(url)
    try {
      const cache = await window.caches.open(ATTACHMENT_CACHE)
      if (await cache.match(url)) return
      const resp = await window.fetch(url, { credentials: 'include' })
      if (resp.ok) {
        await cache.put(url, resp.clone())
        warmed += 1
      }
    } catch {
      // A transient reachability failure can retry on the next warm trigger.
      _attachmentWarmAttempted.delete(url)
    }
  }))
  return warmed
}

function persistQuerySnapshot(key: QueryKey, data: unknown): void {
  if (!Array.isArray(key)) return
  if (key[0] === 'chats' && Array.isArray(data)) {
    void saveLocalChats(data as Chat[])
    return
  }
  if (key[0] === 'chat' && typeof key[1] === 'string' && data) {
    void saveLocalChat(data as Chat)
    return
  }
  if (key[0] === 'topics' && typeof key[1] === 'string' && Array.isArray(data)) {
    void saveLocalTopics(key[1], data as Topic[])
    return
  }
  if (
    key[0] === 'messages' &&
    typeof key[1] === 'string' &&
    (key.length === 2 || key.length === 3) &&
    Array.isArray(data)
  ) {
    const topicId = typeof key[2] === 'string' ? key[2] : null
    void saveLocalMessagesSnapshot(key[1], topicId, data as Message[])
  }
}

export function installLocalReadStorePersistence(qc: QueryClient): void {
  if (_persistenceInstalled) return
  _persistenceInstalled = true
  // Begin the low-priority complete-history backfill as soon as the local read
  // model is installed. This keeps main.tsx's critical boot path unchanged.
  window.setTimeout(startOfflineArchiveAutoSync, 0)
  qc.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated') return
    const state = event.query.state
    if (state.status !== 'success' || state.data === undefined) return
    const key = event.query.queryKey
    const timerKey = JSON.stringify(key)
    const existing = _persistTimers.get(timerKey)
    if (existing !== undefined) window.clearTimeout(existing)
    const timer = window.setTimeout(() => {
      _persistTimers.delete(timerKey)
      persistQuerySnapshot(key, state.data)
    }, 250)
    _persistTimers.set(timerKey, timer)
  })
}
